<template>
  <p v-if="passwordError" role="alert">{{ passwordError.message }}</p>
  <p v-if="completed" role="status">Your password changed. Please sign in again.</p>
  <a-form
    v-if="!completed && !passwordError?.recoveryRequired"
    :model="form"
    :rules="rules"
    :wrapperCol="{ span: 24 }"
    @finish="updatePassword"
    :style="{ width: '400px' }"
  >
    <a-form-item name="oldPwd">
      <a-input-password
        autocomplete="current-password"
        placeholder="Old password"
        v-model:value="form.oldPwd"
      />
    </a-form-item>

    <a-form-item name="newPwd" :extra="passwordGuidance">
      <a-input-password
        autocomplete="new-password"
        placeholder="New password"
        v-model:value="form.newPwd"
      />
    </a-form-item>

    <a-form-item name="confirmPwd">
      <a-input-password
        autocomplete="new-password"
        placeholder="Confirm password"
        v-model:value="form.confirmPwd"
      />
    </a-form-item>

    <a-button :loading="isSubmitting" :disabled="completed" type="primary" htmlType="submit">
      Update
    </a-button>
  </a-form>
</template>

<script>
import {
  validateStrongPassword,
  validateCognitoPassword,
  cognitoPasswordGuidance,
} from '@/utils/validators';
import { isCognitoEnabled, isNativeCognitoEnabled } from '@/shared/auth/cognitoFeatureFlag';
import { cognitoClient } from '@/shared/auth/cognitoClient';
import { AppContext } from '@/models/AppContext';
import { logout } from '@/shared/auth/session';
import { resolveHttpErrorMessage } from '@/http/http-error';

export default {
  name: 'PasswordForm',
  data() {
    return {
      isSubmitting: false,
      completed: false,
      passwordError: null,
      passwordGuidance: isCognitoEnabled
        ? cognitoPasswordGuidance
        : 'Must contain numeric and character, length from 6 to 20.',
      form: {
        oldPwd: '',
        newPwd: '',
        confirmPwd: '',
      },
      rules: {
        oldPwd: [{ required: true, message: 'Old password is required.' }],
        newPwd: [
          { required: true, message: 'New password is required.' },
          {
            validator: isCognitoEnabled ? validateCognitoPassword : validateStrongPassword,
          },
        ],
        confirmPwd: [
          {
            required: true,
            message: 'Please confirm your password',
          },
          {
            validator: this.validateConfirmPassword,
          },
        ],
      },
    };
  },
  methods: {
    /**
     * Update user password
     * After updated, navigate to login page to ask the user to login again
     * @param values
     */
    async updatePassword(values) {
      if (this.isSubmitting || this.completed || this.passwordError?.recoveryRequired) return;
      this.isSubmitting = true;
      this.passwordError = null;
      try {
        const sessionVersion = cognitoClient.sessionVersion();
        const { oldPwd, newPwd } = values;
        await this.$http.put(this.$api.updatePassword, { oldPwd, newPwd });
        this.completed = true;
        this.form = { oldPwd: '', newPwd: '', confirmPwd: '' };
        this.$message.success('Update password successfully, please login again.', 5);
        if (isNativeCognitoEnabled && sessionVersion !== cognitoClient.sessionVersion()) return;
        try {
          await logout(AppContext.SolCentre);
        } catch {
          this.$message.error(
            'Your password changed, but sign-out did not complete. Close this session and sign in again.',
          );
        }
      } catch (e) {
        this.passwordError = {
          message: resolveHttpErrorMessage(e, 'Password change could not be confirmed.'),
          recoveryRequired: e?.recoveryRequired === true,
        };
        if (this.passwordError.recoveryRequired)
          this.form = { oldPwd: '', newPwd: '', confirmPwd: '' };
        this.$message.error(this.passwordError.message);
      } finally {
        this.isSubmitting = false;
      }
    },
    validateConfirmPassword(rule, value) {
      if (!value || this.form.newPwd === value) {
        return Promise.resolve();
      }
      return Promise.reject("Two passwords you enter doesn't match");
    },
  },
};
</script>

<style scoped></style>
